import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MessageCircleIcon } from 'lucide-react';
import { WhatsAppIcon } from './WhatsAppIcon';
interface NightCollaborationsProps {
  whatsappUrl: string;
}
interface BrandItem {
  name: string;
  logo: string;
  /** Per-logo scale factor for visual weight balance (default 1) */
  scale?: number;
  /** Optional CSS filter for color tinting */
  filter?: string;
  /** Optional text color for text-only fallback logos */
  textColor?: string;
}
function BrandCard({
  brand
}: {
  brand: BrandItem;
}) {
  const [imgError, setImgError] = useState(false);
  const scale = brand.scale ?? 1;
  return <div className="flex items-center justify-center h-28 md:h-32 bg-white border border-stone/10 rounded-xl px-6 md:px-8" data-id="element-154">
      {brand.logo && !imgError ? <img src={brand.logo} alt={`${brand.name} logo`} className="w-full object-contain transition-all duration-300" style={{
      maxHeight: `${scale * 4}rem`,
      filter: brand.filter || 'none'
    }} onError={() => setImgError(true)} data-id="element-155" /> : <span className="font-body font-bold tracking-[0.2em] uppercase text-base md:text-lg text-center" style={{
      color: brand.textColor || 'rgba(15, 23, 42, 0.9)'
    }} data-id="element-156">
          {brand.name}
        </span>}
    </div>;
}
export function NightCollaborations({
  whatsappUrl
}: NightCollaborationsProps) {
  const clubs: BrandItem[] = [{
    name: 'UNVRS',
    logo: 'https://cdn.magicpatterns.com/uploads/h1x8z6vRXoNocdL5a7CYdR/unvrs-ibiza-logo-white.jpg',
    scale: 1.3
  }, {
    name: 'Hï Ibiza',
    logo: '',
    textColor: '#7c3aed'
  }, {
    name: 'Amnesia',
    logo: 'https://cdn.magicpatterns.com/uploads/6sGecseVZ44Ji9yFnJUh97/Logo_Amnesia_Ibiza.jpg',
    scale: 1.2
  }, {
    name: 'Pacha Ibiza',
    logo: '',
    textColor: '#e11d48'
  }, {
    name: 'DC-10',
    logo: 'https://cdn.magicpatterns.com/uploads/7vDXm9UVS6KPTCwSsQrbUo/Dc10_logo_with_black_background_and_white_text.jpg',
    scale: 1.1
  }, {
    name: 'Ushuaïa Ibiza',
    logo: 'https://cdn.magicpatterns.com/uploads/ok27at5BXE69tSaESYupLY/ushuaia.png',
    scale: 0.85
  }];
  const restaurants: BrandItem[] = [{
    name: 'Lío Ibiza',
    logo: 'https://cdn.magicpatterns.com/uploads/6RXhnYWgfGXH9JixwW66Le/logo.png',
    scale: 1.15
  }, {
    name: 'Zuma',
    logo: 'https://cdn.magicpatterns.com/uploads/cWR2o38hnHVQHcjhKpfLvo/Zuma-Ibiza-2022-07-29-081924.jpg',
    scale: 1.0
  }, {
    name: 'Beso Beach',
    logo: 'https://cdn.magicpatterns.com/uploads/immukR3zV6pmPNBed8mokL/logo-besobeachibiza-welcometoibiza.jpg',
    scale: 1.0
  }, {
    name: 'ROTO',
    logo: 'https://cdn.magicpatterns.com/uploads/mE4hMwgR7y3xgVhdZn2Wbh/roto_ibiza_logo.png',
    scale: 1.0
  }, {
    name: 'Casa Maca',
    logo: 'https://cdn.magicpatterns.com/uploads/91cLJUC32vvR17bdVFg829/Casa-Maca.jpg',
    scale: 1.1
  }, {
    name: 'El Carnicero',
    logo: 'https://cdn.magicpatterns.com/uploads/cbW1ENkpunDoYAGHHNwb4v/el-carnicero-logo.png',
    scale: 1.0
  }];
  const hotels: BrandItem[] = [{
    name: 'ME by Meliá',
    logo: 'https://cdn.magicpatterns.com/uploads/q6jADeSQSUpJKvUicw8kjG/ME-by-Melia.jpg',
    scale: 1.15
  }, {
    name: 'Nobu Ibiza',
    logo: 'https://cdn.magicpatterns.com/uploads/hrjpcPbiB4gRF3TDxq3Qxk/_Nobu.jpg',
    scale: 1.15
  }, {
    name: 'Marriott',
    logo: 'https://cdn.magicpatterns.com/uploads/i5BFnriaX2riRAE7tnSX8k/Marriott_hotels_logo14.svg.png',
    scale: 1.1
  }];
  return <section className="py-16 md:py-24 bg-white border-t border-stone/10" data-id="element-157">
      <div className="mx-auto max-w-5xl px-5" data-id="element-158">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="text-center max-w-3xl mx-auto mb-16" data-id="element-159">
          <span className="text-gold-subtle text-sm font-semibold tracking-wider uppercase mb-3 block" data-id="element-160">
            Trusted transport for Ibiza's best-known night plans
          </span>
          <h2 className="text-3xl md:text-4xl font-heading text-navy mb-6" data-id="element-161">
            Our nightlife and dining collaborations in Ibiza
          </h2>
          <p className="text-slate text-lg leading-relaxed" data-id="element-162">
            We collaborate with some of Ibiza's leading clubs, restaurants and
            hotels to provide smooth, premium transport for dinners, nightlife
            plans, drop-offs and return journeys across the island.
          </p>
        </motion.div>

        {/* Logos Grid */}
        <div className="space-y-12 mb-16" data-id="element-163">
          {/* Clubs */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} data-id="element-164">
            <h3 className="text-center text-sm font-medium text-slate uppercase tracking-widest mb-6" data-id="element-165">
              Clubs
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-3xl mx-auto" data-id="element-166">
              {clubs.map((club, i) => <BrandCard key={i} brand={club} data-id="element-167" />)}
            </div>
          </motion.div>

          {/* Restaurants */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} data-id="element-168">
            <h3 className="text-center text-sm font-medium text-slate uppercase tracking-widest mb-6" data-id="element-169">
              Restaurants
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-3xl mx-auto" data-id="element-170">
              {restaurants.map((restaurant, i) => <BrandCard key={i} brand={restaurant} data-id="element-171" />)}
            </div>
          </motion.div>

          {/* Hotels */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.3
        }} data-id="element-172">
            <h3 className="text-center text-sm font-medium text-slate uppercase tracking-widest mb-6" data-id="element-173">
              Hotels
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-3xl mx-auto" data-id="element-174">
              {hotels.map((hotel, i) => <BrandCard key={i} brand={hotel} data-id="element-175" />)}
            </div>
          </motion.div>
        </div>

        {/* Copy Below Logos */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.3
      }} className="text-center max-w-2xl mx-auto mb-16" data-id="element-176">
          <p className="text-navy font-medium text-lg mb-4" data-id="element-177">
            We work with Ibiza's leading nightlife, dining and hospitality
            destinations to make arrivals, departures and return pickups easier
            for guests across the island.
          </p>
          <p className="text-slate text-sm leading-relaxed" data-id="element-178">
            From dinner reservations to club nights and late return journeys, we
            help coordinate transport with fixed pricing and reliable local
            drivers.
          </p>
        </motion.div>

        {/* CTA Block */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.4
      }} className="bg-warm-cream border border-stone/10 rounded-2xl p-8 md:p-10 text-center max-w-3xl mx-auto" data-id="element-179">
          <h3 className="text-xl md:text-2xl font-heading text-navy mb-6" data-id="element-180">
            Planning dinner, club and return transport on the same night?
          </h3>
          <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center space-x-2 bg-whatsapp hover:bg-whatsapp-hover text-white px-8 py-4 rounded-full font-medium transition-all shadow-lg shadow-whatsapp/20 mb-4" data-id="element-181">
            <WhatsAppIcon className="w-5 h-5" data-id="element-182" />
            <span data-id="element-183">Get night transfer price on WhatsApp</span>
          </a>
          <p className="text-sm text-slate font-medium" data-id="element-184">
            Fixed price · Local drivers · Return pickup available
          </p>
        </motion.div>
      </div>
    </section>;
}